import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PRODUCT_API_URL, { PRODUCT_API_HEADERS } from '../../../../api/constants/productApi.constant.api';
import {
  parseBrands,
  parseModels,
  parseYears,
  parseVersions,
  parseFitments,
  getEanFromProducts,
} from '../../../../lib/product-api.parse.utils';
import {
  Overlay,
  Modal,
  Header,
  Title,
  CloseButton,
  Content,
  Instructions,
  DropdownRow,
  DropdownContainer,
  DropdownLabel,
  DropdownSelect,
  DropdownIcon,
  SearchButtonContainer,
  Footer,
  CancelButton,
  SearchButton,
  LoadingSpinner,
  Stats,
  Stat,
  ErrorMessage,
} from './BulkSearchModal.styled';

const FETCH_TIMEOUT = 8000;

const fetchWithTimeout = async (url, timeout = FETCH_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...PRODUCT_API_HEADERS,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

const BulkSearchModal = ({ isVisible, onClose, onSearch }) => {
  const { t } = useTranslation();
  const apiUrl = PRODUCT_API_URL();

  // Selection state
  const [vendor, setVendor] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [version, setVersion] = useState('');
  const [fitment, setFitment] = useState('');

  // Options state
  const [vendors, setVendors] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [versions, setVersions] = useState([]);
  const [fitments, setFitments] = useState([]);

  // Loading state
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loadingFitments, setLoadingFitments] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // EAN count for display
  const [eanCount, setEanCount] = useState(0);

  // Fetch vendors on mount and when modal opens
  useEffect(() => {
    if (!isVisible) return;
    
    const fetchVendors = async () => {
      setLoadingVendors(true);
      setError(null);
      try {
        const response = await fetchWithTimeout(`${apiUrl}req=vendor`);
        const parsed = parseBrands(response);
        setVendors(parsed);
      } catch (err) {
        setError(t('errorFetchingBrands') || 'Error fetching brands');
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, [apiUrl, isVisible, t]);

  // Fetch models when vendor changes
  useEffect(() => {
    if (!vendor) {
      setModels([]);
      return;
    }

    const fetchModels = async () => {
      setLoadingModels(true);
      setError(null);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vendor)}&req=model`
        );
        const parsed = parseModels(response);
        setModels(parsed);
      } catch (err) {
        setError(t('errorFetchingModels') || 'Error fetching models');
        setModels([]);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
  }, [apiUrl, vendor, t]);

  // Fetch years when model changes
  useEffect(() => {
    if (!vendor || !model) {
      setYears([]);
      return;
    }

    const fetchYears = async () => {
      setLoadingYears(true);
      setError(null);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vendor)}&model=${encodeURIComponent(model)}&req=year`
        );
        const parsed = parseYears(response);
        setYears(parsed);
      } catch (err) {
        setError(t('errorFetchingYears') || 'Error fetching years');
        setYears([]);
      } finally {
        setLoadingYears(false);
      }
    };

    fetchYears();
  }, [apiUrl, vendor, model, t]);

  // Fetch versions when year changes
  useEffect(() => {
    if (!vendor || !model || !year) {
      setVersions([]);
      return;
    }

    const fetchVersions = async () => {
      setLoadingVersions(true);
      setError(null);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vendor)}&model=${encodeURIComponent(model)}&year=${encodeURIComponent(year)}&req=version`
        );
        const parsed = parseVersions(response);
        setVersions(parsed);
      } catch (err) {
        setError(t('errorFetchingVersions') || 'Error fetching versions');
        setVersions([]);
      } finally {
        setLoadingVersions(false);
      }
    };

    fetchVersions();
  }, [apiUrl, vendor, model, year, t]);

  // Fetch fitments when version changes
  useEffect(() => {
    if (!vendor || !model || !year || !version) {
      setFitments([]);
      setEanCount(0);
      return;
    }

    const fetchFitments = async () => {
      setLoadingFitments(true);
      setError(null);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vendor)}&model=${encodeURIComponent(model)}&year=${encodeURIComponent(year)}&version=${encodeURIComponent(version)}&req=frontSizeDesignation`
        );
        const parsed = parseFitments(response);
        setFitments(parsed);
      } catch (err) {
        setError(t('errorFetchingFitments') || 'Error fetching fitments');
        setFitments([]);
      } finally {
        setLoadingFitments(false);
      }
    };

    fetchFitments();
  }, [apiUrl, vendor, model, year, version, t]);

  // Fetch EAN count when fitment changes (for preview)
  useEffect(() => {
    if (!vendor || !model || !year || !version || !fitment) {
      setEanCount(0);
      return;
    }

    const fetchEanCount = async () => {
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vendor)}&model=${encodeURIComponent(model)}&year=${encodeURIComponent(year)}&version=${encodeURIComponent(version)}&frontSizeDesignation=${encodeURIComponent(fitment)}&products=true&articles=true`
        );
        const eans = getEanFromProducts(response);
        setEanCount(eans.length);
      } catch (err) {
        setEanCount(0);
      }
    };

    fetchEanCount();
  }, [apiUrl, vendor, model, year, version, fitment]);

  const resetForm = useCallback(() => {
    setVendor('');
    setModel('');
    setYear('');
    setVersion('');
    setFitment('');
    setModels([]);
    setYears([]);
    setVersions([]);
    setFitments([]);
    setEanCount(0);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleVendorChange = (e) => {
    const value = e.target.value;
    setVendor(value);
    setModel('');
    setYear('');
    setVersion('');
    setFitment('');
    setYears([]);
    setVersions([]);
    setFitments([]);
    setEanCount(0);
    setError(null);
  };

  const handleModelChange = (e) => {
    const value = e.target.value;
    setModel(value);
    setYear('');
    setVersion('');
    setFitment('');
    setVersions([]);
    setFitments([]);
    setEanCount(0);
    setError(null);
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    setYear(value);
    setVersion('');
    setFitment('');
    setFitments([]);
    setEanCount(0);
    setError(null);
  };

  const handleVersionChange = (e) => {
    const value = e.target.value;
    setVersion(value);
    setFitment('');
    setEanCount(0);
    setError(null);
  };

  const handleFitmentChange = (e) => {
    const value = e.target.value;
    setFitment(value);
    setError(null);
  };

  const handleSearch = async () => {
    if (!vendor || !model || !year || !version || !fitment) return;

    setLoadingSearch(true);
    setError(null);

    try {
      const response = await fetchWithTimeout(
        `${apiUrl}vendor=${encodeURIComponent(vendor)}&model=${encodeURIComponent(model)}&year=${encodeURIComponent(year)}&version=${encodeURIComponent(version)}&frontSizeDesignation=${encodeURIComponent(fitment)}&products=true&articles=true`
      );
      const eans = getEanFromProducts(response);

      if (eans.length > 0) {
        onSearch(eans, {
          vendor,
          model,
          year,
          version,
          fitment,
          eanCount: eans.length,
        });
        handleClose();
      } else {
        setError(t('noProductsFound') || 'No products found for this selection');
      }
    } catch (err) {
      setError(t('errorSearching') || 'Error searching for products');
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const canSearch = vendor && model && year && version && fitment && !loadingSearch;
  const isLoadingDropdown = loadingVendors || loadingModels || loadingYears || loadingVersions || loadingFitments;

  if (!isVisible) return null;

  return (
    <Overlay isVisible={isVisible} onClick={handleClose}>
      <Modal isVisible={isVisible} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <Header>
          <Title>
            <i className="mdi mdi-car" />
            {t('searchByVehicle') || 'Izberite znamko in model avtomobila'}
          </Title>
          <CloseButton onClick={handleClose}>
            <i className="mdi mdi-close" />
          </CloseButton>
        </Header>

        <Content>
          <Instructions>
            {t('vehicleSearchInstructions') || 'Izberite znamko in model avtomobila'}
            {isLoadingDropdown && <span style={{ marginLeft: 8, opacity: 0.6 }}>({t('loading') || 'Nalaganje'}...)</span>}
          </Instructions>

          <DropdownRow>
            <DropdownContainer disabled={loadingVendors}>
              <DropdownLabel>{t('brand') || 'Znamka'}</DropdownLabel>
              <DropdownSelect
                value={vendor}
                onChange={handleVendorChange}
                disabled={loadingVendors}
              >
                <option value="">{loadingVendors ? '...' : t('selectBrand') || 'Izberi'}</option>
                {vendors.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon>
                <i className="mdi mdi-chevron-down" />
              </DropdownIcon>
            </DropdownContainer>

            <DropdownContainer disabled={!vendor || loadingModels}>
              <DropdownLabel>{t('model') || 'Model'}</DropdownLabel>
              <DropdownSelect
                value={model}
                onChange={handleModelChange}
                disabled={!vendor || loadingModels}
              >
                <option value="">{loadingModels ? '...' : t('selectModel') || 'Izberi'}</option>
                {models.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon>
                <i className="mdi mdi-chevron-down" />
              </DropdownIcon>
            </DropdownContainer>

            <DropdownContainer disabled={!model || loadingYears}>
              <DropdownLabel>{t('year') || 'Leto'}</DropdownLabel>
              <DropdownSelect
                value={year}
                onChange={handleYearChange}
                disabled={!model || loadingYears}
              >
                <option value="">{loadingYears ? '...' : t('selectYear') || 'Izberi'}</option>
                {years.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon>
                <i className="mdi mdi-chevron-down" />
              </DropdownIcon>
            </DropdownContainer>

            <DropdownContainer disabled={!year || loadingVersions}>
              <DropdownLabel>{t('version') || 'Verzija'}</DropdownLabel>
              <DropdownSelect
                value={version}
                onChange={handleVersionChange}
                disabled={!year || loadingVersions}
              >
                <option value="">{loadingVersions ? '...' : t('selectVersion') || 'Izberi'}</option>
                {versions.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon>
                <i className="mdi mdi-chevron-down" />
              </DropdownIcon>
            </DropdownContainer>

            <DropdownContainer disabled={!version || loadingFitments}>
              <DropdownLabel>{t('fitment') || 'Fitment'}</DropdownLabel>
              <DropdownSelect
                value={fitment}
                onChange={handleFitmentChange}
                disabled={!version || loadingFitments}
              >
                <option value="">{loadingFitments ? '...' : t('selectFitment') || 'Izberi'}</option>
                {fitments.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon>
                <i className="mdi mdi-chevron-down" />
              </DropdownIcon>
            </DropdownContainer>

            <SearchButtonContainer>
              <SearchButton onClick={handleSearch} disabled={!canSearch}>
                {loadingSearch ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <i className="mdi mdi-magnify" />
                    {t('search') || 'Išči'}
                  </>
                )}
              </SearchButton>
            </SearchButtonContainer>
          </DropdownRow>

          {eanCount > 0 && (
            <Stats>
              <Stat>
                <i className="mdi mdi-tire" />
                <span>
                  {t('productsFound') || 'Najdenih izdelkov'}: <strong>{eanCount}</strong>
                </span>
              </Stat>
            </Stats>
          )}

          {error && (
            <ErrorMessage>
              <i className="mdi mdi-alert-circle" />
              {error}
            </ErrorMessage>
          )}
        </Content>

        <Footer>
          <CancelButton onClick={handleClose}>
            {t('cancel') || 'Prekliči'}
          </CancelButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};

BulkSearchModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};

export default BulkSearchModal;
